import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, CheckCircle2, AlertCircle, Filter, History } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { id: '', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'failed', label: 'Failed' },
];

const TriggerHistoryTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('today');
  const [chatbotFilter, setChatbotFilter] = useState('');
  const [chatbots, setChatbots] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter) params.set('filter', filter);
      if (chatbotFilter) params.set('chatbot_id', chatbotFilter);
      const res = await fetch(`${API_URL}/api/automation/chatbots/trigger-history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setItems(data.data || []);
    } catch (e) {
      console.error('trigger-history load failed', e);
    } finally {
      setLoading(false);
    }
  }, [filter, chatbotFilter]);

  const loadChatbots = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/automation/chatbots`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setChatbots(data.data || []);
    } catch (e) {
      console.error('chatbots load failed', e);
    }
  }, []);

  useEffect(() => {
    loadChatbots();
  }, [loadChatbots]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4" data-testid="trigger-history-tab">
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <Filter size={16} className="text-gray-500" />
        {FILTERS.map((f) => (
          <button
            key={f.id || 'all'}
            onClick={() => setFilter(f.id)}
            data-testid={`history-filter-${f.id || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === f.id
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={chatbotFilter}
            onChange={(e) => setChatbotFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            data-testid="history-chatbot-select"
          >
            <option value="">All chatbots</option>
            {chatbots.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <button
            onClick={load}
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            data-testid="history-refresh-btn"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="animate-spin text-teal-600" size={28} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <History size={26} className="text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">No chatbot executions yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Trigger history populates as incoming messages match your rules.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="trigger-history-table">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Time</th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Matched text</th>
                <th className="px-4 py-3 text-left">Rule</th>
                <th className="px-4 py-3 text-left">Reply</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {new Date(it.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-gray-800 font-mono text-xs">{it.phone || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={it.matchedText}>
                    {it.matchedText || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-800">{it.chatbotName || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={it.replyMessage}>
                    {it.replyMessage || '—'}
                  </td>
                  <td className="px-4 py-3">
                    {it.success ? (
                      <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                        <CheckCircle2 size={14} /> Success
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-red-700 text-xs"
                        title={it.error || ''}
                      >
                        <AlertCircle size={14} /> Failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TriggerHistoryTab;
